import { memo, useMemo } from 'react';

import type { AssetId, ChainAddressId } from '@infinex/asset-config';

import {
  ChainTokenBalanceListUI,
  type ChainTokenBalanceListUIProps,
} from './ChainTokenBalanceListUI';
import { useUsdSortedChainAssetBalances } from './hooks/useUsdSortedChainAssetBalances';
import { addIsDisabledAndSort } from './utils/addIsDisabledAndSort';

export type ChainTokenBalanceListProps = Omit<
  ChainTokenBalanceListUIProps,
  'sortedTokenBalanceKeys'
> & {
  /** hide small balances */
  hideSmallBalances?: boolean;
  checkIsDisabled?: (
    chainAddressId: ChainAddressId,
    assetId?: AssetId
  ) =>
    | {
        disabledLabel?: string;
        disabledDetails?: string;
      }
    | false;
  /** Flag indicating if selection limit has been reached */
  isAtSelectionLimit?: boolean;
  /** Maximum number of items that can be selected in multi-select mode */
  selectionLimit?: number;
  /** Custom message to show when selection limit is reached */
  selectionLimitMessage?: string;
  hideSectionTitle?: boolean;
  /** Rendered in place of the list when the filtered list has no rows */
  emptyState?: React.ReactNode;
  className?: string;
};

/**
 * A list of balances at a chain asset level
 *
 * @param props - The props for the token list
 * @returns The token list
 */
export const ChainTokenBalanceList = memo(
  ({
    sectionTitle,
    tokenBalances,
    hideSmallBalances = false,
    highlightSelected = false,
    selectedAsset,
    onSelection,
    buildTestId,
    checkIsDisabled,
    listClassName,
    titleSize = 'base',
    showCustodyViewPill,
    walletView,
    titleAdornment,
    scrollElementRef,
    selectionMode,
    selectedKeys,
    isAtSelectionLimit,
    onToggle,
    selectionLimit,
    selectionLimitMessage,
    hideSectionTitle = false,
    stickyTitle,
    emptyState,
    showWalletSources,
    className,
    rowGap,
  }: ChainTokenBalanceListProps) => {
    const sortedTokenBalanceKeys = useUsdSortedChainAssetBalances(
      tokenBalances,
      hideSmallBalances
    );

    const sortedWithDisabled = useMemo(
      () =>
        addIsDisabledAndSort({
          sortedTokenBalanceKeys,
          isAtSelectionLimit,
          selectedKeys,
          tokenBalances,
          checkIsDisabled,
          selectionLimit,
          selectionLimitMessage,
        }),
      [
        sortedTokenBalanceKeys,
        isAtSelectionLimit,
        selectedKeys,
        tokenBalances,
        checkIsDisabled,
        selectionLimit,
        selectionLimitMessage,
      ]
    );

    if (sortedTokenBalanceKeys.length === 0) {
      return emptyState ? <>{emptyState}</> : null;
    }

    return (
      <ChainTokenBalanceListUI
        showWalletSources={showWalletSources}
        sectionTitle={sectionTitle}
        hideSectionTitle={hideSectionTitle}
        stickyTitle={stickyTitle}
        tokenBalances={tokenBalances}
        highlightSelected={highlightSelected}
        selectedAsset={selectedAsset}
        onSelection={onSelection}
        sortedTokenBalanceKeys={sortedWithDisabled}
        buildTestId={buildTestId}
        listClassName={listClassName}
        className={className}
        titleSize={titleSize}
        showCustodyViewPill={showCustodyViewPill}
        walletView={walletView}
        titleAdornment={titleAdornment}
        scrollElementRef={scrollElementRef}
        selectionMode={selectionMode}
        selectedKeys={selectedKeys}
        onToggle={onToggle}
        rowGap={rowGap}
      />
    );
  }
);
